import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useSearchParams } from 'react-router-dom';
import { AdminUser, adminListUsers, adminUpdateUser, readAxiosDetail } from './api_calls';
import { AppContext } from '../../context/context';
import UserDetailDrawer from './UserDetailDrawer';
import { refreshAdminCapability } from '../../services/auth/adminAccess';

import { Icon } from '@liftango/ui-icons';
export default function UsersTab() {
  const context = useContext(AppContext);
  const currentUserId = context?.userData?.userDetails?.userId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [roleChangeTarget, setRoleChangeTarget] = useState<AdminUser | null>(null);
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);

  const [drawerUser, setDrawerUser] = useState<AdminUser | null>(null);
  const [params, setParams] = useSearchParams();
  const deepLinkedUserId = params.get('userId');

  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<'name' | 'email' | 'role' | 'joined'>('name');

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminListUsers();
      setUsers(data);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  // Honor ?userId=… deep-link from the analytics drawer.
  useEffect(() => {
    if (!deepLinkedUserId || users.length === 0) return;
    const match = users.find((u) => u.user_id === deepLinkedUserId);
    if (match) setDrawerUser(match);
  }, [deepLinkedUserId, users]);

  const clearDeepLink = () => {
    if (!params.has('userId')) return;
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('userId');
      return next;
    }, { replace: true });
  };

  const handleConfirmRoleChange = async () => {
    if (!roleChangeTarget) return;
    const nextIsAdmin = roleChangeTarget.role !== 'admin';
    setRoleChangeLoading(true);
    setError(null);
    try {
      const updated = await adminUpdateUser(roleChangeTarget.user_id, { is_admin: nextIsAdmin });
      setUsers((prev) => prev.map((u) => (u.user_id === updated.user_id ? updated : u)));
      setSessionNotice(
        'Role updated. Session claims may be stale until re-login, and admin visibility has been refreshed.'
      );
      void refreshAdminCapability();
      setRoleChangeTarget(null);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setRoleChangeLoading(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const query = search.trim().toLowerCase();
  const sortFn = (a: AdminUser, b: AdminUser): number => {
    if (sortOrder === 'email') return a.email.localeCompare(b.email);
    if (sortOrder === 'role') return a.role.localeCompare(b.role);
    if (sortOrder === 'joined') return a.created_at.localeCompare(b.created_at);
    return (a.full_name || a.email).localeCompare(b.full_name || b.email);
  };
  const visibleUsers = users
    .filter(
      (u) =>
        !query ||
        u.email.toLowerCase().includes(query) ||
        u.full_name?.toLowerCase().includes(query) ||
        (u.customer_name && u.customer_name.toLowerCase().includes(query))
    )
    .sort(sortFn);

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {sessionNotice && (
        <Alert severity="info" onClose={() => setSessionNotice(null)} sx={{ mb: 2 }}>
          {sessionNotice}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search users…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <Icon name="magnifying-glass" size={20} style={{ marginRight: 4, color: '#52565e' }} />,
          }}
          sx={{ flex: 1 }}
        />
        <Select
          size="small"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value as typeof sortOrder)}
          sx={{ minWidth: { xs: '100%', sm: 140 } }}
        >
          <MenuItem value="name">Sort: Name</MenuItem>
          <MenuItem value="email">Sort: Email</MenuItem>
          <MenuItem value="role">Sort: Role</MenuItem>
          <MenuItem value="joined">Sort: Joined</MenuItem>
        </Select>
      </Stack>

      {visibleUsers.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No users found.
        </Typography>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Email</strong>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <strong>Full Name</strong>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <strong>Customer</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Status</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Role</strong>
                </TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <strong>Joined</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleUsers.map((user) => {
                const isSelf = user.user_id === currentUserId;
                return (
                  <TableRow
                    key={user.user_id}
                    hover
                    onClick={() => setDrawerUser(user)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {user.email}
                      </Typography>
                      {/* On xs only, fold secondary fields under email */}
                      <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 0.25 }}>
                        {user.full_name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {user.full_name}
                          </Typography>
                        )}
                        {user.customer_name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {user.customer_name}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2">{user.full_name || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Typography variant="body2" color="text.secondary">
                        {user.customer_name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.disabled ? 'Disabled' : 'Active'}
                        size="small"
                        color={user.disabled ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Chip
                        label={user.role === 'admin' ? 'Admin' : 'User'}
                        size="small"
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        variant={user.role === 'admin' ? 'filled' : 'outlined'}
                        onClick={isSelf ? undefined : () => setRoleChangeTarget(user)}
                        sx={{ cursor: isSelf ? 'default' : 'pointer' }}
                      />
                      {isSelf && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (you)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(user.created_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UserDetailDrawer
        user={drawerUser}
        onClose={() => {
          setDrawerUser(null);
          clearDeepLink();
        }}
        onUserUpdated={(u) => {
          setUsers((prev) => {
            const previous = prev.find((row) => row.user_id === u.user_id);
            if (previous && previous.role !== u.role) {
              setSessionNotice(
                'Role updated. Session claims may be stale until re-login, and admin visibility has been refreshed.'
              );
              void refreshAdminCapability();
            }
            return prev.map((row) => (row.user_id === u.user_id ? u : row));
          });
        }}
      />

      <Dialog open={Boolean(roleChangeTarget)} onClose={() => setRoleChangeTarget(null)} fullWidth maxWidth="xs" fullScreen={isMobile}>
        <DialogTitle>Change Role</DialogTitle>
        <DialogContent>
          {roleChangeTarget && (
            <Typography>
              {roleChangeTarget.role === 'admin' ? 'Demote' : 'Promote'}{' '}
              <strong>{roleChangeTarget.email}</strong> to{' '}
              <strong>{roleChangeTarget.role === 'admin' ? 'user' : 'admin'}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleChangeTarget(null)} disabled={roleChangeLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleConfirmRoleChange()}
            disabled={roleChangeLoading}
          >
            {roleChangeLoading ? <CircularProgress size={18} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
